/**
 * /tools/calc — the ASO calculator (A7Y-6118).
 *
 * Every figure, label and line on the page is fixed by Yevhen's spec
 * (`ai-dev/marketing/sales/roi-estimate/calculator-spec.md`, 30 Oct 2026, attached
 * to the ticket); this module holds them, and the arithmetic, so the page
 * component only lays them out. Plan prices are read from `PLAN_TIERS`, the
 * table the pricing page renders, so a price change never leaves this page stale.
 *
 * Nothing here leaves the browser: the page computes from what the visitor
 * types and sends none of it anywhere.
 */
import { CURRENCY_CONFIG, PLAN_TIERS, type SupportedCurrency } from '@a7y/types/billing';

export type CalcStore = 'app_store' | 'google_play';
export type CalcPlan = 'basic' | 'pro' | 'elite';

/**
 * Both lift sliders, in percent. The low end is the default, so the smallest
 * estimate is 2 × min. These are assumptions; when measured customer lifts
 * exist, they and the two slider labels change and nothing else.
 */
export const CALC_LIFT = { min: 5, max: 20, step: 1 } as const;

/** The plans offered beside the number: Free has no price, Enterprise is priced per account. */
export const CALC_PLANS: readonly CalcPlan[] = ['basic', 'pro', 'elite'];
export const CALC_DEFAULT_PLAN: CalcPlan = 'pro';

export const CALC_STORES: readonly CalcStore[] = ['app_store', 'google_play'];

export const CALC_SEO = {
  title: 'ASO Calculator: What Better ASO Is Worth to Your App',
  description:
    'Type your monthly installs and what a new user is worth to see what better ASO could bring your app each month, beside the price of an Apptonomy plan.',
} as const;

export const CALC_COPY = {
  h1: 'What is better ASO worth to your app?',
  subline: 'Apptonomy audits your store listing and tells you what to fix, in what order, and why.',
  instructions:
    'Pick your store and type two numbers from your own data. Move the sliders if you see it differently. If you run paid campaigns, add what you pay per install.',
  storeLabel: { app_store: 'App Store', google_play: 'Google Play' } satisfies Record<CalcStore, string>,
  installs: {
    label: 'New installs a month',
    placeholder: 'Installs a month',
    hint: {
      app_store: 'App Store Connect: First Time Downloads.',
      google_play: 'Play Console, Statistics: Store listing acquisitions.',
    } satisfies Record<CalcStore, string>,
    tooltip:
      'Includes your paid installs, which keyword work does not add, so the estimate runs high if many of them are paid.',
  },
  value: {
    label: 'What a new user is worth to you in the first year',
    placeholder: 'Value per new user',
    hint: {
      app_store:
        "Take it from your MMP or finance team. App Store Connect's Proceeds per Download covers in-app sales only, not ads.",
      google_play:
        'Take it from your MMP or finance team. Play Console shows revenue per paying user, not per new user.',
    } satisfies Record<CalcStore, string>,
  },
  cpi: {
    label: 'What you pay per install today',
    placeholder: 'Cost per install',
    hint: "From your ad platform or MMP, in the currency above. Empty if you don't run paid campaigns.",
  },
  privacy: 'Your numbers stay in your browser. Nothing you type is sent to us.',
  searchSlider: {
    label: 'More installs from search',
    note: `Our assumption: keyword work adds ${CALC_LIFT.min}–${CALC_LIFT.max}% more installs.`,
  },
  conversionSlider: {
    label: 'Better conversion',
    note: 'Our assumption for one icon or screenshot change that wins its test.',
  },
  tag: 'Estimate',
  planLabel: 'Apptonomy plan',
  cta: 'Run the audit',
  ctaHref: '/scan',
  ctaLine:
    'The audit also asks AI assistants for apps like yours and shows whether they name yours, and where.',
} as const;

/** A typed amount, or null while the field is empty or not a usable number. */
export function parseAmount(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

/** The value is taken at two decimals as typed: 0.333 counts as 0.33. */
export function atTwoDecimals(value: number): number {
  return Math.trunc(value * 100 + 1e-9) / 100;
}

export interface CalcInput {
  installs: number;
  value: number;
  /** Slider 1, percent. */
  searchLift: number;
  /** Slider 2, percent. */
  conversionLift: number;
  /** Field 3, or null when empty. */
  cpi: number | null;
  currency: SupportedCurrency;
  plan: CalcPlan;
}

export interface CalcResult {
  /** installs × (slider 1 + slider 2), rounded. The lifts add; they are never multiplied. */
  extraInstalls: number;
  /** The value as counted: two decimals as typed. */
  value: number;
  /** extraInstalls × value, rounded to whole currency units. */
  monthly: number;
  // source: calculator-spec.md, Result, block 1 tooltip (low uses 5% + 5%, high 20% + 20%).
  /** The same at 2 × min and 2 × max, installs rounded before × value. */
  low: number;
  high: number;
  planPrice: number;
  /** monthly ÷ plan price, both in the same currency. */
  multiple: number;
  /** Only when field 3 is filled. Never added to the headline. */
  cpi: { from: number; to: number; dropPercent: number } | null;
}

function monthlyAt(installs: number, totalLift: number, value: number): number {
  return Math.round(Math.round((installs * totalLift) / 100) * value);
}

export function planMonthlyPrice(plan: CalcPlan, currency: SupportedCurrency): number {
  return PLAN_TIERS[plan].pricing.currencies[currency].monthly / 100;
}

export function computeEstimate(input: CalcInput): CalcResult {
  const value = atTwoDecimals(input.value);
  const extraInstalls = Math.round((input.installs * (input.searchLift + input.conversionLift)) / 100);
  const monthly = Math.round(extraInstalls * value);
  const planPrice = planMonthlyPrice(input.plan, input.currency);
  const b = input.conversionLift;
  return {
    extraInstalls,
    value,
    monthly,
    low: monthlyAt(input.installs, CALC_LIFT.min * 2, value),
    high: monthlyAt(input.installs, CALC_LIFT.max * 2, value),
    planPrice,
    multiple: monthly / planPrice,
    cpi:
      input.cpi === null
        ? null
        : {
            from: input.cpi,
            to: input.cpi / (1 + b / 100),
            dropPercent: (b / (100 + b)) * 100,
          },
  };
}

export function formatCount(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

export function formatMoney(amount: number, currency: SupportedCurrency, decimals: 0 | 2 = 0): string {
  return `${CURRENCY_CONFIG[currency].symbol}${amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

// source: calculator-spec.md, Result, the multiple between block 1 and block 2.
/** One decimal under 10×, whole numbers from 10×, "<0.1×" below 0.1×. */
export function formatMultiple(multiple: number): string {
  if (multiple < 0.1) return '<0.1×';
  const oneDecimal = Math.round(multiple * 10) / 10;
  if (oneDecimal < 10) return `${oneDecimal.toFixed(1)}×`;
  return `${formatCount(multiple)}×`;
}

/** Every line the result shows, composed from one estimate. */
export function resultLines(input: CalcInput, result: CalcResult) {
  const { currency } = input;
  const money = (amount: number, decimals: 0 | 2 = 0) => formatMoney(amount, currency, decimals);
  return {
    headline: `What better ASO could bring you: ${formatCount(result.extraInstalls)} installs, ${money(result.monthly)} a month`,
    block1: `Better ASO could bring you: ≈ ${money(result.monthly)} a month.`,
    block1Tooltip: `The installs you miss in one month, valued at what those users are worth to you in their first year. Between ${money(result.low)} and ${money(result.high)} across our full ranges.`,
    multiple: `${formatMultiple(result.multiple)} the price`,
    planPrice: `${money(result.planPrice)} a month`,
    cpi:
      result.cpi === null
        ? null
        : {
            title: `Your cost per install: ${money(result.cpi.to, 2)}`,
            line: `from ${money(result.cpi.from, 2)}, ${result.cpi.dropPercent.toFixed(1)}% less`,
            tooltip: `A better-converting store page turns more of your ad taps into installs: ${money(result.cpi.from, 2)} ÷ (1 + ${input.conversionLift}%) = ${money(result.cpi.to, 2)}.`,
          },
    formula: `${formatCount(input.installs)} × (${input.searchLift}% + ${input.conversionLift}%) = ${formatCount(result.extraInstalls)} extra installs; ${formatCount(result.extraInstalls)} × ${money(result.value, 2)} = ${money(result.monthly)} a month`,
  };
}
